<template>
  <page-section
    ref="scrollContainer"
    class="bg-neutral-0 flex-1 overflow-auto base-scrollbar"
  >
    <!-- loading state -->
    <div v-if="loading">
      <PerformanceChartsLoader class="max-w-screen-xl mx-auto" />
    </div>
    <div
      v-else-if="error"
      class="flex flex-col items-center justify-center mt-20"
    >
      <img
        src="/content/images/brightbid/robot-error.svg"
        alt="Error occurred"
      />
      <p class="text-center font-medium mt-4 bb-text-headers text-base">Content failed to load</p>
      <p class="text-center mt-2 w-1/2">
        We couldn’t render some data. Please try refreshing the page or checking your internet connection. If the issue
        persists,
        <a
          class="cursor-pointer"
          @click="openSupport"
          >contact support</a
        >.
      </p>
    </div>
    <div
      v-else
      class="relative h-full overflow-y-auto pb-40 pt-4"
    >
      <div class="mb-4">
        <h4 class="bb-text-default">Performance Metrics</h4>
      </div>
      <div>
        <!-- breadcrumbs -->
        <div class="flex center flex-wrap gap-y-2">
          <arrow-breadcrumbs
            v-for="(item, index) in breadcrumbs"
            :key="index"
            :breadcrumb="item"
            :rounded="index === 0"
            @click="scrollToChart(item.links)"
          />
        </div>
        <div class="grid grid-cols-5 text-bb-neutral-gray pt-7">
          <p class="font-medium col-span-2">This week vs previous</p>
          <p class="font-light col-span-3 pl-5">
            <span class="font-medium">Last 6 months</span> ({{ formatDate(startDate) }} - {{ formatDate(endDate) }})
          </p>
        </div>
        <!-- charts -->
        <div
          v-if="performanceCharts.length"
          class="flex-col center space-y-3 pt-3"
        >
          <performance-chart-card
            v-for="(item, index) in performanceCharts"
            :key="index"
            :chart="item"
            :currency="currency"
          />
        </div>
      </div>
    </div>
  </page-section>
</template>

<script>
import ArrowBreadcrumbs from '@/components/breadcrumb/ArrowBreadcrumb.vue'
import PerformanceChartCard from '@/components/card/PerformanceChartCard.vue'
import PageSection from '@/components/base/page/PageSection.vue'
import PerformanceChartsLoader from './PerformanceChartsLoader.vue'
import { METRIC_KEY } from '@/core/constants'
import { mapState, mapGetters, mapActions } from 'vuex'
import dayjs from 'dayjs'

export default {
  name: 'PerformanceCharts',
  components: { ArrowBreadcrumbs, PerformanceChartCard, PageSection, PerformanceChartsLoader },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('troubleshootingTree', ['currency', 'error', 'performanceCharts']),
    ...mapGetters('troubleshootingTree', ['startDate', 'endDate', 'hideConversionValueMetrics']),
    ...mapGetters('site', ['siteId']),
    breadcrumbs() {
      let breadcrumbs = [
        {
          label: 'ROAS',
          links: [METRIC_KEY.roas],
          hidden: this.hideConversionValueMetrics,
          selected: false,
        },
        {
          label: 'Cost' + (this.hideConversionValueMetrics ? '' : ' & Conv. Value'),
          links: [METRIC_KEY.cost, METRIC_KEY.conv_value],
          selected: false,
        },
        {
          label: 'AOV',
          links: [METRIC_KEY.aov],
          hidden: this.hideConversionValueMetrics,
          selected: false,
        },
        {
          label: 'CPA & Conv.',
          links: [METRIC_KEY.cpa, METRIC_KEY.conv],
          selected: false,
        },
        {
          label: 'Conv. Rate & Clicks',
          links: [METRIC_KEY.conv_rate, METRIC_KEY.clicks],
          selected: false,
        },
        {
          label: 'CTR & Impressions',
          links: [METRIC_KEY.ctr, METRIC_KEY.impressions],
          selected: false,
        },
        {
          label: 'Imp. Share & Avg CPC',
          links: [METRIC_KEY.impression_share, METRIC_KEY.avg_cpc],
          selected: false,
        },
      ]

      breadcrumbs = breadcrumbs.filter(item => !item.hidden)
      return breadcrumbs.map(item => this.mapAnomaly(item))
    },
  },
  methods: {
    ...mapActions('troubleshootingTree', ['highlightCharts']),
    formatDate(date) {
      return dayjs(date).format('DD/MM/YYYY')
    },
    mapAnomaly(item) {
      let colors = []
      item.links.forEach(link => {
        const chart = this.performanceCharts.find(chart => chart.link === link)
        if (chart && chart.anomaly) {
          colors.push(chart.comparisonColor)
        }
      })

      if (colors.length) {
        item.anomaly = 'text-bb-brand-purple'

        if ((colors.length > 1 && colors[0] === colors[1]) || colors.length === 1) {
          item.anomaly = colors[0]
        }
      }

      return item
    },
    scrollToChart(links) {
      const scrollContainer = this.$refs.scrollContainer.$el
      this.highlightCharts({ links, isSelected: true })

      setTimeout(() => {
        this.highlightCharts({ links, isSelected: false })
      }, 2000)

      this.$nextTick(() => {
        const element = document.getElementById(links[0])

        if (element && scrollContainer) {
          const containerTop = scrollContainer.getBoundingClientRect().top
          const elementTop = element.getBoundingClientRect().top
          const scrollOffset = elementTop - containerTop

          scrollContainer.scrollTo({
            top: scrollContainer.scrollTop + scrollOffset,
            behavior: 'smooth',
          })
        }
      })
    },
    openSupport() {
      if (window.Intercom) {
        window.Intercom('show')
        return
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
